import fetchApi from "../lib/fetchApi";
import { AxiosLikeCsvResponse, CsvCellValue, DownloadCsvSource, GenerateCsvFileOptions } from "../types/DownloadCsvTypes";
import { formatDateToCompactYYYYMMDD } from "./formatters/DateFormatter";

const isAxiosLikeCsvResponse = (value: unknown): value is AxiosLikeCsvResponse =>
  typeof value === "object" && value !== null && "data" in value;

const escapeCsvValue = (value: CsvCellValue): string => {
  const stringValue = String(value ?? "");
  if (/[;"\r\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const buildCsvContent = <TRow>( headers: string[], rows: TRow[], mapRow: (row: TRow) => CsvCellValue[], trailingSemicolon = true, includeBom = true ): string => {
  const suffix = trailingSemicolon ? ";" : "";
  const csvLines: string[] = [];

  csvLines.push(`${headers.map(escapeCsvValue).join(";")}${suffix}`);

  rows.forEach((row) => {
    const cells = mapRow(row);
    csvLines.push(`${cells.map(escapeCsvValue).join(";")}${suffix}`);
  });

  const csvBody = csvLines.join("\r\n");
  return includeBom ? `\uFEFF${csvBody}` : csvBody;
};

export const downloadCsvFile = (source: DownloadCsvSource, fileName: string, mimeType = "text/csv;charset=utf-8"): void => {
  if (!fileName?.trim()) throw new Error("A valid fileName is required");

  let parts: BlobPart[];

  if (isAxiosLikeCsvResponse(source)) {
    if (source.data == null) throw new Error("No CSV data found in response");
    parts = [source.data];
  } else if (Array.isArray(source)) {
    parts = source;
  } else {
    parts = [source];
  }

  if (parts.length === 0) throw new Error("CSV content is empty");

  let url: string | null = null;
  let link: HTMLAnchorElement | null = null;

  try {
    const blob = new Blob(parts, { type: mimeType });
    url = window.URL.createObjectURL(blob);

    link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
  } catch {
    throw new Error("Failed to download CSV file");
  } finally {
    if (link && document.body.contains(link)) {
      document.body.removeChild(link);
    }
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  }
};

export const generateCsvFile = async <TRow>({ headers, rows, mapRow, fileBaseName, trailingSemicolon = true, includeBom = true, audit }: GenerateCsvFileOptions<TRow>): Promise<void> => {
  const csvContent = buildCsvContent(headers, rows, mapRow, trailingSemicolon, includeBom);
  downloadCsvFile(csvContent, `${fileBaseName}_${formatDateToCompactYYYYMMDD()}.csv`);

  if (!audit?.entity) return;

  try {
    await fetchApi.post("/auditlogs/add", audit);
  } catch (error) {
    console.error("Error adding CSV export audit log", error);
  }
};

